import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import type { Language } from '@/types'
import { localStorageGet, localStorageSet } from '@/lib/storage'
import { getTextDirection, isRTL } from '@/lib/rtl'

const LANGUAGE_STORAGE_KEY = 'qr-menu-language'

// ============================================================================
// Context Type
// ============================================================================

interface LanguageContextValue {
  language: Language
  setLanguage: (lang: Language) => void
  isRTL: boolean
  textDirection: 'rtl' | 'ltr'
  availableLanguages: Language[]
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

// ============================================================================
// Language Provider
// ============================================================================

interface LanguageProviderProps {
  children: ReactNode
  defaultLanguage?: Language
  enabledLanguages?: Language[]
}

export function LanguageProvider({
  children,
  defaultLanguage = 'fr',
  enabledLanguages = ['fr', 'ar', 'en'],
}: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(() => {
    return localStorageGet(LANGUAGE_STORAGE_KEY, defaultLanguage)
  })

  // Update HTML dir/lang attributes when language changes
  useEffect(() => {
    const dir = getTextDirection(language)
    document.documentElement.setAttribute('dir', dir)
    document.documentElement.setAttribute('lang', language)
  }, [language])

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang)
    localStorageSet(LANGUAGE_STORAGE_KEY, lang)
  }, [])

  const value: LanguageContextValue = {
    language,
    setLanguage,
    isRTL: isRTL(language),
    textDirection: getTextDirection(language),
    availableLanguages: enabledLanguages,
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

// ============================================================================
// Hook to consume the context
// ============================================================================

export function useLanguageContext(): LanguageContextValue {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguageContext must be used within a LanguageProvider')
  }
  return context
}
